import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import { useInfiniteQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import ReversePaymentConfirmationModal from "src/main/Billing/Payments/ReversePaymentConfirmationModal"
import SettlePaymentModal from "src/main/Billing/Payments/SettlePaymentModal"

import Button from "src/components/Button"
import Modal from "src/components/Modal"
import Tooltip from "src/components/Tooltip"

import { queryInvoiceTxns } from "src/api/Billing/Payments"

import InvoiceItemsTable from "./InvoiceItemsTable"
import InvoiceTransactionsTable from "./InvoiceTransactionsTable"
import { toLegacyInvoiceShape } from "./legacyInvoiceShape"

const ManageInvoiceModal = ({
  invoice,
  isOpen,
  onClose,
  refreshPayments,
  renderDetails,
  marinaSlug,
  settleDisabled = false,
  paymentMethods = { cards: [] },
  canManage = false,
  onReverseClick = () => {},
}) => {
  const { marinaSlug: contextMarinaSlug } = useMarina()
  const resolvedMarinaSlug = marinaSlug || contextMarinaSlug

  const [isSettling, setIsSettling] = useState(false)
  const [showReverseConfirmation, setShowReverseConfirmation] = useState(false)
  const invoiceId = invoice?.id
  const isSettled = Boolean(invoice?.isSettled)

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ["invoice_txns", invoiceId, resolvedMarinaSlug],
      queryFn: ({ pageParam = 1 }) =>
        queryInvoiceTxns({
          invoiceId,
          marinaSlug: resolvedMarinaSlug,
          page: pageParam,
        }),
      getNextPageParam: (lastPage, pages) =>
        lastPage.length ? pages.length + 1 : undefined,
      enabled: Boolean(invoiceId) && isOpen,
      refetchOnWindowFocus: false,
    })

  useEffect(() => {
    if (!isFetchingNextPage && hasNextPage) fetchNextPage()
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const invoiceTxns = useMemo(
    () => (data?.pages || []).flatMap((p) => p),
    [data]
  )

  // Same split the legacy PaymentModal performs.
  const items = useMemo(
    () =>
      invoiceTxns.filter(
        (i) => (i.amount !== 0 && i.txn.product_sale) || i.txn.product_return
      ),
    [invoiceTxns]
  )

  const transactions = useMemo(
    () =>
      invoiceTxns.filter(
        (i) =>
          i.txn.stripe_payment ||
          i.txn.stripe_refund ||
          i.txn.offline_payment ||
          i.txn.offline_refund
      ),
    [invoiceTxns]
  )

  const reverseDisabledMessage = invoice?.reversePaymentDisabledMessage
  const isReversible = !reverseDisabledMessage

  const renderReverseButton = () => {
    if (!canManage) return null

    const button = (
      <Button
        variant="tertiary"
        disabled={!isReversible}
        onClick={() => {
          onReverseClick()
          setShowReverseConfirmation(true)
        }}
      >
        Reverse payment
      </Button>
    )

    if (isReversible) return button
    return (
      <Tooltip text={reverseDisabledMessage} placement="top" variant="dark">
        {button}
      </Tooltip>
    )
  }

  if (!invoice) return null

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="large">
        <Modal.Header>
          <div>
            <h1 className="m-0 mb-2 text-lg font-semibold">Manage Invoice</h1>
            <div className="text-sm text-gray-600">{invoiceId}</div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <h2 className="text-base font-semibold">Products / Services</h2>
          <InvoiceItemsTable items={items} isPaymentSettled={isSettled} />

          <div className="pt-5">
            <h2 className="mb-4 text-base font-semibold">Details</h2>
            {renderDetails({ invoice, isSettled })}
          </div>

          <div className="pt-5">
            <h2 className="mb-4 text-base font-semibold">
              Transaction History
            </h2>
            <InvoiceTransactionsTable transactions={transactions} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-4">
            {isSettled ? (
              renderReverseButton()
            ) : (
              <Button
                variant="primary"
                isLoading={isFetching}
                disabled={settleDisabled}
                onClick={() => setIsSettling(true)}
              >
                Settle now
              </Button>
            )}
            <Button variant="tertiary" onClick={onClose}>
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/*
        SettlePaymentModal must be a child rather than a sibling because
        headlessUI does not support multiple open sibling dialogs:
        https://github.com/tailwindlabs/headlessui/issues/426#issuecomment-1059185316
      */}
      <SettlePaymentModal
        payment={toLegacyInvoiceShape(invoice)}
        paymentMethods={paymentMethods}
        isOpen={isSettling}
        onClose={() => setIsSettling(false)}
        onPaymentSettle={() => {
          setIsSettling(false)
          refreshPayments()
          onClose()
        }}
      />
      <ReversePaymentConfirmationModal
        isOpen={showReverseConfirmation}
        onClose={() => setShowReverseConfirmation(false)}
        onReverseSuccess={() => {
          setShowReverseConfirmation(false)
          refreshPayments()
          onClose()
        }}
        paymentId={invoiceId}
        examples={invoice?.reversalExamples}
      />
    </>
  )
}

ManageInvoiceModal.propTypes = {
  invoice: PropTypes.shape({
    id: PropTypes.string,
    balance: PropTypes.number,
    totalAmount: PropTypes.number,
    billingContainerId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    allowsPartialRefund: PropTypes.bool,
    isSettled: PropTypes.bool,
    isRefund: PropTypes.bool,
    reversePaymentDisabledMessage: PropTypes.string,
    reversalExamples: PropTypes.arrayOf(PropTypes.string),
    paymentMethod: PropTypes.object,
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  refreshPayments: PropTypes.func.isRequired,
  renderDetails: PropTypes.func.isRequired,
  marinaSlug: PropTypes.string.isRequired,
  settleDisabled: PropTypes.bool,
  paymentMethods: PropTypes.shape({ cards: PropTypes.array }),
  canManage: PropTypes.bool,
  onReverseClick: PropTypes.func,
}

export default ManageInvoiceModal
